import {ExperimentNames, getFormattedFullLocation, hasImage} from '@wix/wix-events-commons-statics'
import {useExperiments, useTranslation} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React from 'react'
import {getEventDateInformation, isMultiDay} from '../../../../../../../../../commons/selectors/event'
import {useWidgetState} from '../../../../../../hooks/state-provider'
import {useVisibilityStyles} from '../../../../../../hooks/use-visibility-styles'
import {
  getDayMonthText,
  getEventsForDay,
  getSelectedDate,
  getSelectedEventId,
} from '../../../../../../selectors/calendar-layout'
import {
  getEventById,
  getMultiDayText,
  getRibbonData,
  shouldShowPromotionalBadge,
} from '../../../../../../selectors/events'
import {isMembersVisible, isRibbonVisible} from '../../../../../../selectors/settings'
import {EventImage} from '../../../../../event-image'
import {LinkToPage} from '../../../../../link-to-page'
import {Members} from '../../../../../members'
import {PromotionalBadge} from '../../../../../promotional-badge'
import {Ribbon} from '../../../../../ribbon'
import {RsvpButton} from '../../../../../rsvp-button'
import {SocialBar} from '../../../../../social-bar'
import {PopupHeader} from '../header'
import {MonthlyCalendarEventDetailsProps} from './interfaces'
import s from './monthly-calendar-event-details.scss'

export const MonthlyCalendarEventDetails = ({
  shareFontSize,
  closeMonthlyCalendarPopup,
  closeMonthlyCalendarEvent,
}: MonthlyCalendarEventDetailsProps) => {
  const {t} = useTranslation()
  const {experiments} = useExperiments()
  const {
    allBreakpoints: {isPromotionalBadgeOnImage, isPromotionalBadgeInEventInfo},
  } = useVisibilityStyles()

  const selectedDate = useWidgetState(getSelectedDate)
  const selectedEventId = useWidgetState(getSelectedEventId)
  const dateText = useWidgetState(state => getDayMonthText(state, selectedDate))
  const events = useWidgetState(state => getEventsForDay(state, selectedDate))
  const event = useWidgetState(state => getEventById(state, selectedEventId))
  const showRibbon = useWidgetState(state => isRibbonVisible(state, event))
  const showMembers = useWidgetState(state => isMembersVisible(state, event))
  const multiDay = useWidgetState(state => isMultiDay(state, event))
  const multiDayText = useWidgetState(state => getMultiDayText(state, event.id, selectedDate, t))
  const fullDate = useWidgetState(state => getEventDateInformation(state, event.id).fullDate)
  const hasRibbon = useWidgetState(state => getRibbonData(state, event).visible)
  const showPromotionalBadge = useWidgetState(state => shouldShowPromotionalBadge(state, event))
  const promotionalBadgesEnabled = experiments.enabled(ExperimentNames.PromotionalBadges)

  return (
    <div className={s.container} data-hook="calendar-event-details">
      <PopupHeader
        eventDetails
        text={dateText}
        onClose={closeMonthlyCalendarPopup}
        onBack={events.length > 1 ? closeMonthlyCalendarEvent : undefined}
        t={t}
      />
      <div className={s.eventDetailsContent}>
        {hasImage(event) && (
          <div className={s.image}>
            <EventImage event={event} noLQIP />
            {promotionalBadgesEnabled && showPromotionalBadge && isPromotionalBadgeOnImage() && (
              <div className={s.promotionalBadgeOnImage}>
                <PromotionalBadge onImage event={event} />
              </div>
            )}
          </div>
        )}
        {promotionalBadgesEnabled && showPromotionalBadge && isPromotionalBadgeInEventInfo() && (
          <div className={classNames(s.promotionalBadge, {[s.margin]: !showRibbon || !hasRibbon})}>
            <PromotionalBadge event={event} />
          </div>
        )}
        {showRibbon && <Ribbon event={event} className={s.ribbon} calendar />}
        {multiDay && <div className={s.multiDayTitle}>{multiDayText}</div>}
        <div className={s.title} data-hook="title">
          <LinkToPage event={event}>{event.title}</LinkToPage>
        </div>
        <div className={s.fullDate} data-hook="date">
          {fullDate}
        </div>
        <div className={s.location} data-hook="location">
          {getFormattedFullLocation(event)}
        </div>
        {showMembers && (
          <div className={s.members} data-hook="members">
            <Members event={event} calendarLayout={true} />
          </div>
        )}
        {event.description && (
          <div className={s.description} data-hook="description">
            {event.description}
          </div>
        )}
        <div className={s.share} data-hook="share">
          <div className={s.shareText}>{t('shareEventText')}</div>
          <SocialBar t={t} spacing={7} event={event} iconFontSize={shareFontSize} newColors={false} row hideTitle />
        </div>
      </div>
      <RsvpButton event={event} dataHook="rsvp-button" minWidth={88} minHeight={32} containerClassName={s.button} />
    </div>
  )
}
